/** One destination resolver for forms, suggestions, and bookmarked search URLs. */
const nativeSearch = (() => {
  const canonicalQuery = (query) => query.trim().replace(/\s+/g, ' ')
    .replace(/\bsplit[\s\u2010-\u2014-]*head\b/gi, 'split head');
  const normalizeQuery = (query) => canonicalQuery(query).toLowerCase();
  const template = document.getElementById('native-search-popular-products');
  const redirects = new Map();

  function addRedirect(query, destination) {
    if (typeof query !== 'string' || typeof destination !== 'string' || !destination.trim()) return;
    try {
      const url = new URL(destination, window.location.origin);
      if (url.protocol !== 'https:' && url.origin !== window.location.origin) return;
      if (!['http:', 'https:'].includes(url.protocol)) return;
      const key = normalizeQuery(query);
      if (key) redirects.set(key, url.href);
    } catch (error) {
      // A malformed authoring value must not prevent ordinary product searches.
    }
  }

  const migrated = document.getElementById('native-search-redirects');
  if (migrated) {
    try {
      JSON.parse(migrated.textContent).forEach((rule) => addRedirect(rule.query, rule.url));
    } catch (error) {
      // The shop metafield and theme-editor rules remain available independently.
    }
  }
  const legacy = window.SDG && window.SDG.Data && window.SDG.Data.search_redirects;
  if (legacy && Array.isArray(legacy.queries) && Array.isArray(legacy.urls)) {
    legacy.queries.forEach((query, index) => addRedirect(query, legacy.urls[index]));
  }
  if (template) {
    template.content.querySelectorAll('.native-search__redirect').forEach((rule) => {
      rule.dataset.queries.split(/\r?\n/).forEach((query) => addRedirect(query, rule.dataset.url));
    });
  }

  return {
    canonicalQuery,
    normalizeQuery,
    redirectUrl: (query) => redirects.get(normalizeQuery(query)),
    resultsUrl(query) {
      const searchUrl = template ? template.dataset.searchUrl : '/search';
      return redirects.get(normalizeQuery(query))
        || `${searchUrl}?q=${encodeURIComponent(canonicalQuery(query))}&type=product`;
    },
  };
})();

/** Shopify Search & Discovery predictive results for both header search forms. */
function nativePredictiveSearch() {
  const template = document.getElementById('native-search-popular-products');
  if (!template) return;

  const forms = document.querySelectorAll('.form-inline--search');
  const { predictiveUrl } = template.dataset;
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const priceFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
  const { canonicalQuery, normalizeQuery, resultsUrl } = nativeSearch;
  const maxSuggestions = 10;
  const searchRules = Array.from(template.content.querySelectorAll('.native-search__rule'));
  const productResults = new Map();
  const productCacheLifetime = 60000;
  let openState = null;

  function localUrl(path) {
    try {
      const url = new URL(path, window.location.origin);
      return url.origin === window.location.origin ? url.href : null;
    } catch (error) {
      return null;
    }
  }

  function makeHeading(label) {
    const heading = document.createElement('h2');
    heading.className = 'native-search__heading';
    heading.textContent = label;
    return heading;
  }

  function makeQuery(query) {
    const link = document.createElement('a');
    link.className = 'native-search__query';
    link.href = resultsUrl(query.text);
    link.textContent = query.text;
    return link;
  }

  function makeProduct(product) {
    // Shopify folds a matched variant's id into `product.url`'s "variant" param for queries that
    // match a size, such as "split head". `product.variants` can still carry an unrelated/default
    // variant even when no size matched, so only trust it when its id round-trips through that url;
    // otherwise fall back to the product-level result (and avoid showing "Default Title").
    const href = localUrl(product.url);
    if (!href) return null;
    const url = new URL(href);
    const variantId = url.searchParams.get('variant');
    const variant = variantId && product.variants
      ? product.variants.find((item) => String(item.id) === variantId)
      : null;

    const link = document.createElement('a');
    link.className = 'native-search__product';
    link.href = url.href;
    const productImage = (variant && variant.image) || product.image;
    if (productImage) {
      const image = document.createElement('img');
      image.src = productImage;
      image.alt = '';
      image.width = 60;
      image.height = 60;
      image.loading = 'lazy';
      link.appendChild(image);
    }

    const details = document.createElement('span');
    details.className = 'native-search__product-details';
    const title = document.createElement('span');
    title.className = 'native-search__product-title';
    title.textContent = product.title;
    details.appendChild(title);

    if (variant && variant.title && variant.title !== 'Default Title') {
      const size = document.createElement('span');
      size.className = 'native-search__product-variant';
      size.textContent = variant.title;
      details.appendChild(size);
    }

    const price = Number(variant ? variant.price : product.price);
    if (!Number.isNaN(price)) {
      const priceLabel = document.createElement('span');
      priceLabel.className = 'native-search__product-price';
      const comparePrice = Number(variant
        ? variant.compare_at_price : product.compare_at_price_min);
      if (comparePrice > price) {
        const compare = document.createElement('s');
        compare.textContent = priceFormatter.format(comparePrice);
        priceLabel.appendChild(compare);
      }
      priceLabel.appendChild(document.createTextNode(priceFormatter.format(price)));
      details.appendChild(priceLabel);
    }
    link.appendChild(details);
    return link;
  }

  function searchRule(query) {
    return searchRules.find((item) => normalizeQuery(item.dataset.query || '') === normalizeQuery(query));
  }

  function curatedProducts(query) {
    const rule = searchRule(query);
    return rule ? rule.querySelectorAll('.native-search__product') : [];
  }

  function curatedSuggestions(query) {
    const rule = searchRule(query);
    if (!rule || !rule.dataset.suggestions) return [];
    const seen = new Set();
    return rule.dataset.suggestions.split(/\r?\n/).map((term) => term.trim()).filter((term) => {
      const key = normalizeQuery(term);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, maxSuggestions)
      .map((text) => ({ text }));
  }

  function renderProducts(column, query, products, hovered = false) {
    column.replaceChildren();
    const curated = curatedProducts(query);
    column.appendChild(makeHeading(curated.length || hovered ? 'Popular Products' : 'Products'));
    if (curated.length) {
      curated.forEach((product) => column.appendChild(product.cloneNode(true)));
    } else {
      products.slice(0, 6).forEach((product) => {
        const link = makeProduct(product);
        if (link) column.appendChild(link);
      });
    }
    if (!column.querySelector('.native-search__product')) {
      const empty = document.createElement('p');
      empty.className = 'native-search__empty';
      empty.textContent = 'No products found.';
      column.appendChild(empty);
    }
  }

  function fetchProducts(query) {
    const key = normalizeQuery(query);
    const cached = productResults.get(key);
    if (!cached || Date.now() - cached.createdAt >= productCacheLifetime) {
      const params = new URLSearchParams({ q: canonicalQuery(query) });
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '6');
      const request = fetch(`${predictiveUrl}?${params}`, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          const results = data.resources && data.resources.results;
          return (results && results.products) || [];
        });
      if (productResults.size >= 100) productResults.delete(productResults.keys().next().value);
      productResults.set(key, { request, createdAt: Date.now() });
      request.catch(() => {
        const current = productResults.get(key);
        if (current && current.request === request) productResults.delete(key);
      });
    }
    return productResults.get(key).request;
  }

  function close(state) {
    state.request += 1;
    state.productRequest += 1;
    clearTimeout(state.timer);
    state.panel.hidden = true;
    state.input.setAttribute('aria-expanded', 'false');
    if (openState === state) openState = null;
  }

  function position(state) {
    if (state.panel.hidden) return;
    const rect = state.form.getBoundingClientRect();
    const mobile = window.innerWidth < 768;
    const width = mobile ? window.innerWidth : Math.min(720, window.innerWidth - 24);
    state.panel.style.width = `${width}px`;
    state.panel.style.left = `${mobile ? 0 : Math.max(12, rect.right - width)}px`;
    state.panel.style.top = `${Math.min(rect.bottom, window.innerHeight - 80)}px`;
  }

  function render(state, data, query) {
    if (state.input.value.trim() !== query) return;
    state.productRequest += 1;
    const results = (data.resources && data.resources.results) || {};
    const queries = curatedSuggestions(query);
    if (!queries.length) queries.push(...(results.queries || []).slice(0, maxSuggestions));
    const products = results.products || [];
    state.panel.replaceChildren();

    const columns = document.createElement('div');
    columns.className = 'native-search__columns';
    const suggestions = document.createElement('div');
    suggestions.className = 'native-search__suggestions';
    suggestions.appendChild(makeHeading('Suggestions'));
    const productColumn = document.createElement('div');
    productColumn.className = 'native-search__products';
    renderProducts(productColumn, query, products);

    function showSuggestionProducts(item, link) {
      const term = item.text.trim();
      if (!term) return;
      state.productRequest += 1;
      const request = state.productRequest;
      suggestions.querySelectorAll('.native-search__query').forEach((suggestion) => {
        suggestion.classList.toggle('is-active', suggestion === link);
      });
      if (curatedProducts(term).length || normalizeQuery(term) === normalizeQuery(query)) {
        renderProducts(productColumn, term, products, true);
        return;
      }
      fetchProducts(term).then((matchingProducts) => {
        if (request !== state.productRequest || state.panel.hidden
          || state.input.value.trim() !== query) return;
        renderProducts(productColumn, term, matchingProducts, true);
      }).catch(() => {
        // Keep the current products visible if a hover request fails.
      });
    }

    if (queries.length) {
      queries.forEach((item) => {
        const link = makeQuery(item);
        link.addEventListener('mouseenter', () => showSuggestionProducts(item, link));
        link.addEventListener('focus', () => showSuggestionProducts(item, link));
        suggestions.appendChild(link);
      });
    } else {
      const empty = document.createElement('p');
      empty.className = 'native-search__empty';
      empty.textContent = 'No suggestions found.';
      suggestions.appendChild(empty);
    }
    columns.appendChild(suggestions);

    columns.appendChild(productColumn);
    state.panel.appendChild(columns);

    const allResults = document.createElement('a');
    allResults.className = 'native-search__all-results';
    allResults.href = resultsUrl(query);
    allResults.textContent = `View all results for “${query}”`;
    state.panel.appendChild(allResults);

    if (openState && openState !== state) close(openState);
    state.panel.hidden = false;
    state.input.setAttribute('aria-expanded', 'true');
    openState = state;
    position(state);
  }

  forms.forEach((form, index) => {
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    const panel = document.createElement('div');
    panel.id = `native-search-results-${index}`;
    panel.className = 'native-search__panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', 'Search suggestions');
    panel.hidden = true;
    document.body.appendChild(panel);
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-controls', panel.id);
    input.setAttribute('aria-expanded', 'false');
    const state = {
      form, input, panel, request: 0, productRequest: 0, timer: null,
    };

    function search() {
      const query = input.value.trim();
      state.request += 1;
      state.productRequest += 1;
      const { request } = state;
      clearTimeout(state.timer);
      if (!query) {
        close(state);
        return;
      }
      state.timer = setTimeout(() => {
        const params = new URLSearchParams({ q: canonicalQuery(query) });
        params.set('resources[type]', 'query,product');
        params.set('resources[limit]', String(maxSuggestions));
        params.set('resources[limit_scope]', 'each');
        fetch(`${predictiveUrl}?${params}`, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
            return response.json();
          })
          .then((data) => {
            if (request === state.request) render(state, data, query);
          })
          .catch(() => {
            if (request === state.request) close(state);
          });
      }, 180);
    }

    input.addEventListener('input', search);
    input.addEventListener('focus', search);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close(state);
      if (event.key === 'ArrowDown' && !panel.hidden) {
        const firstLink = panel.querySelector('a');
        if (firstLink) {
          event.preventDefault();
          firstLink.focus();
        }
      }
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close(state);
        input.focus();
      }
    });
    form.addEventListener('submit', () => close(state));
    document.addEventListener('pointerdown', (event) => {
      if (!form.contains(event.target) && !panel.contains(event.target)) close(state);
    });
    window.addEventListener('resize', () => position(state));
    window.addEventListener('scroll', () => position(state), { passive: true });
  });
}

nativePredictiveSearch();

// The legacy header handler cancels submission when no redirect is configured.
// Use the named query field so hidden search parameters cannot become the query.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!form.matches('.form-inline--search, .search-header')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const query = form.elements.q.value.trim();
  if (!query) return;

  window.location.assign(nativeSearch.resultsUrl(query));
}, true);

// Unbxd applied redirects after arriving on /search as well as on form submit.
if (document.querySelector('.collection--search')) {
  const searchUrl = new URL(window.location.href);
  const query = searchUrl.searchParams.get('q') || '';
  const destination = nativeSearch.redirectUrl(query);
  if (destination && destination !== window.location.href) window.location.replace(destination);
  else if (query !== nativeSearch.canonicalQuery(query)) {
    searchUrl.searchParams.set('q', nativeSearch.canonicalQuery(query));
    window.location.replace(searchUrl.href);
  }
}

// Keep search filters and the selected sort together when a sort option changes.
document.addEventListener('click', (event) => {
  const option = event.target.closest('.collection--search .js-sort-item');
  if (!option) return;
  const form = document.getElementById('FacetFiltersForm');
  if (!form) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const params = new URLSearchParams(new FormData(form));
  params.set('sort_by', option.dataset.value);
  window.location.assign(`${window.location.pathname}?${params}`);
}, true);
